import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  DollarSign, Megaphone, Phone, Mail, Linkedin, Calendar, FileText, Award,
  Users2, FileEdit, Mic, Video, Share2, Newspaper, Camera, Handshake, Briefcase,
  Building2, Sparkles, Star, GraduationCap,
} from 'lucide-react';

const SECTIONS = [
  {
    title: 'Partnership Revenue Goals',
    color: 'magenta',
    fields: [
      { key: 'total_partnership_revenue', label: 'Total Partnership Revenue', icon: Briefcase, prefix: '$' },
      { key: 'media_revenue', label: 'Media Partnership', icon: Camera, prefix: '$' },
      { key: 'strategic_revenue', label: 'Strategic Partnership', icon: Building2, prefix: '$' },
      { key: 'sponsorship_revenue', label: 'Sponsorship', icon: Star, prefix: '$' },
      { key: 'marketplace_revenue', label: 'Marketplace Partner', icon: Sparkles, prefix: '$' },
      { key: 'gala_revenue', label: 'Gala Partner', icon: Award, prefix: '$' },
      { key: 'workforce_revenue', label: 'Workforce Partner', icon: GraduationCap, prefix: '$' },
      { key: 'revenue', label: 'Overall Revenue (legacy)', icon: DollarSign, prefix: '$' },
    ],
  },
  {
    title: 'Activity Goals',
    color: 'navy',
    fields: [
      { key: 'outreach', label: 'Outreach', icon: Megaphone },
      { key: 'calls', label: 'Calls', icon: Phone },
      { key: 'emails', label: 'Emails', icon: Mail },
      { key: 'linkedin', label: 'LinkedIn Outreach', icon: Linkedin },
      { key: 'meetings', label: 'Meetings', icon: Calendar },
      { key: 'discovery_meetings', label: 'Discovery Meetings', icon: Handshake },
      { key: 'proposals', label: 'Proposals', icon: FileText },
      { key: 'closures', label: 'Total Closures', icon: Award },
      { key: 'media_partner_closures', label: 'Media Partner Closures', icon: Award },
      { key: 'strategic_partnerships', label: 'Strategic Partnerships', icon: Users2 },
    ],
  },
  {
    title: 'Content Goals',
    color: 'magenta',
    fields: [
      { key: 'blog', label: 'Blog Articles', icon: FileEdit },
      { key: 'podcast', label: 'Podcast Episodes', icon: Mic },
      { key: 'webinar', label: 'Webinars', icon: Video },
      { key: 'social_media', label: 'Social Media Posts', icon: Share2 },
      { key: 'newsletter', label: 'Newsletters', icon: Mail },
      { key: 'press_release', label: 'Press Releases', icon: Newspaper },
      { key: 'video_production', label: 'Video Production', icon: Camera },
    ],
  },
];

export default function GoalsPage() {
  const [period, setPeriod] = useState('monthly');
  const [goals, setGoals] = useState(null);

  const load = (p) => api.get(`/goals?period=${p}`).then((r) => setGoals(r.data));
  useEffect(() => { load(period); }, [period]);

  const save = async () => {
    const payload = { ...goals, period };
    delete payload.id;
    delete payload.updated_at;
    await api.put('/goals', payload);
    toast.success(`${period} goals updated`);
  };

  if (!goals) return <div className="text-slate-500 text-sm">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="bg-gradient-to-br from-navy to-navy/95 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-magenta text-[11px] font-semibold tracking-[0.12em] uppercase">Admin Settings</div>
            <h2 className="font-heading text-2xl font-bold mt-1">KPI Goal Center</h2>
            <p className="text-white/70 text-sm mt-2 max-w-md">Set targets by partnership type. All dashboards, scorecards, and progress bars update automatically.</p>
          </div>
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="bg-white/10">
              <TabsTrigger value="monthly" data-testid="goals-monthly" className="data-[state=active]:bg-magenta data-[state=active]:text-white">Monthly</TabsTrigger>
              <TabsTrigger value="quarterly" data-testid="goals-quarterly" className="data-[state=active]:bg-magenta data-[state=active]:text-white">Quarterly</TabsTrigger>
              <TabsTrigger value="annual" data-testid="goals-annual" className="data-[state=active]:bg-magenta data-[state=active]:text-white">Annual</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {SECTIONS.map((section) => (
        <div key={section.title}>
          <h3 className="font-heading text-lg font-bold text-navy mb-3">{section.title}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {section.fields.map((f) => (
              <div key={f.key} className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm" data-testid={`goal-${f.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${section.color === 'navy' ? 'bg-navy/10 text-navy' : 'bg-magenta/10 text-magenta'}`}>
                    <f.icon className="w-4 h-4" />
                  </div>
                  <Label className="text-xs font-semibold text-slate-700">{f.label}</Label>
                </div>
                <div className="relative">
                  {f.prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{f.prefix}</span>}
                  <Input
                    type="number"
                    value={goals[f.key] ?? 0}
                    onChange={(e) => setGoals({ ...goals, [f.key]: parseFloat(e.target.value) || 0 })}
                    className={f.prefix ? 'pl-7' : ''}
                    data-testid={`goal-input-${f.key}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-end sticky bottom-4">
        <Button onClick={save} className="bg-magenta hover:bg-magenta/90 px-8 shadow-lg" data-testid="save-goals">Save Goals</Button>
      </div>
    </div>
  );
}
